import { Operation } from '../../../../common/components/FilterPanel/enum/Operation';
import { Operator } from '../../../../common/components/FilterPanel/enum/Operator';
import { Severity } from '../../../../common/enums/Severity';
import { InvalidUrlError } from '../../../../common/errors/errors';
import { InstanceMapper } from '../../../../common/instances/mappers/InstanceMapper';
import { Instance } from '../../../../common/instances/models/Instance';
import { SeverityMapper } from '../../../../common/mappers/SeverityMapper';
import { BaseGQLService } from '../../../../common/services/BaseGQLService';
import { FilterOperation, FilterOperator } from '../../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../../common/types/pagination';
import { ModelType } from '../../../../common/utils/GQLIdEncoder';
import { InitialInstancePayload } from '../../common/contexts/ReportIssuesContext';
import { IssueMapper } from '../mappers/IssueMapper';
import { Issue } from '../models/Issue';
import { InstanceCounters } from '../models/valueObject/InstanceCounters';

export const DEFAULT_INSTANCES_LIST_PAGE_SIZE = 10;

export const INSTANCE_QUERY_FRAGMENT = `
id
severity
data
filterStatus
number
numberLocal
`;

const ISSUE_QUERY_FRAGMENT = `
number
title
explanation
consequences
access
expertise
resources
kbLink
severity
`;

const INSTANCE_COUNTERS_FRAGMENT = `
highInstances: instances(keptByFilters: $instancesKeptByFilters, filters:{field: severity, value: "HIGH"}) {
  totalCount
}
medInstances: instances(keptByFilters: $instancesKeptByFilters, filters:{field: severity, value: "MEDIUM"}) {
  totalCount
}
lowInstances: instances(keptByFilters: $instancesKeptByFilters, filters:{field: severity, value: "LOW"}) {
  totalCount
}
passedInstances: instances(keptByFilters: $instancesKeptByFilters, filters:{field: severity, value: "PASSED"}) {
  totalCount
}
`;

export const GET_ISSUE_QUERY = `
query ($reportId: ID!, $ruleNumber: String, $includeFilteredRules: Boolean, $instancesKeptByFilters: Boolean) {
  node(id: $reportId) {
    ... on ReportDone {
      rules(includeFilteredRules: $includeFilteredRules, filters: {field: number, operation: EQ, value: $ruleNumber}) {
        edges {
          node {
            ${ISSUE_QUERY_FRAGMENT}
            ${INSTANCE_COUNTERS_FRAGMENT}
            instances(keptByFilters: $instancesKeptByFilters, sorters: [{field: numberLocal, order: ASC}], first: ${DEFAULT_INSTANCES_LIST_PAGE_SIZE}) {
              totalCount
              pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
              }
              edges {
                node {
                  ${INSTANCE_QUERY_FRAGMENT}
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

export const GET_ISSUE_CENTERED_INSTANCES_QUERY = `
query ($reportId: ID!, $ruleNumber: String, $instanceNumberLocal: String, $includeFilteredRules: Boolean, $instancesKeptByFilters: Boolean) {
  node(id: $reportId) {
    ... on ReportDone {
      rules(includeFilteredRules: $includeFilteredRules, filters: {field: number, operation: EQ, value: $ruleNumber}) {
        edges {
          node {
            ${ISSUE_QUERY_FRAGMENT}
            ${INSTANCE_COUNTERS_FRAGMENT}
            previousInstances: instances(
              keptByFilters: $instancesKeptByFilters,
              filters: {field: numberLocal, operation: LT, value: $instanceNumberLocal},
              sorters: [{field: numberLocal, order: ASC}],
              last: ${DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1},
            ) {
              totalCount
              edges {
                cursor
                node {
                  ${INSTANCE_QUERY_FRAGMENT}
                }
              }
            }
            currentInstance: instances(
              keptByFilters: $instancesKeptByFilters,
              filters: {field: numberLocal, operation: EQ, value: $instanceNumberLocal},
              sorters: [{field: numberLocal, order: ASC}],
            ) {
              edges {
                cursor
                node {
                  ${INSTANCE_QUERY_FRAGMENT}
                }
              }
            }
            nextInstances: instances(
              keptByFilters: $instancesKeptByFilters,
              filters: {field: numberLocal, operation: GT, value: $instanceNumberLocal},
              sorters: [{field: numberLocal, order: ASC}],
              first: ${DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1},
            ) {
              totalCount
              edges {
                cursor
                node {
                  ${INSTANCE_QUERY_FRAGMENT}
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

export const GET_ISSUE_INSTANCES_QUERY = `
query (
  $reportId: ID!,
  $ruleNumber: String,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: InstancesFilter,
  $includeFilteredRules: Boolean,
  $instancesKeptByFilters: Boolean
) {
  node(id: $reportId) {
    ... on ReportDone {
      rules(includeFilteredRules: $includeFilteredRules, filters: {field: number, operation: EQ, value: $ruleNumber}) {
        edges {
          node {
            number
            instances(
              keptByFilters: $instancesKeptByFilters,
              first: $first,
              last: $last,
              after: $after,
              before: $before,
              filters: $filters,
              sorters: [{field: numberLocal, order: ASC}],
            ) {
              totalCount
              pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
              }
              edges {
                node {
                  ${INSTANCE_QUERY_FRAGMENT}
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

export class ReportIssueRepository extends BaseGQLService {
  private readonly issueMapper = new IssueMapper();
  private readonly instanceMapper = new InstanceMapper();
  private readonly severityMapper = new SeverityMapper();

  async getIssue(
    reportId: number,
    issueNumber: number,
    includeFilteredItems: boolean,
    initialInstance?: InitialInstancePayload
  ): Promise<{ issue: Issue; initialInstances: PaginationResult<Instance>; instanceCounters: InstanceCounters }> {
    if (initialInstance) {
      return this.getIssueWithCenteredInstances(reportId, issueNumber, includeFilteredItems, initialInstance);
    }
    return this.query(
      GET_ISSUE_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ruleNumber: issueNumber.toString(),
        includeFilteredRules: includeFilteredItems,
        instancesKeptByFilters: includeFilteredItems ? null : true,
      },
      (data) => {
        if (!data?.node?.rules?.edges?.[0]?.node) {
          throw new InvalidUrlError(`Invalid request: could not find Rule ${issueNumber} in Report ${reportId}`);
        }
        const { instances, ...rule } = data.node.rules.edges[0].node;
        return {
          issue: this.issueMapper.mapGQLModel(rule),
          instanceCounters: this.issueMapper.mapInstanceCounters(rule),
          initialInstances: {
            data: instances.edges.map((e: any) => this.instanceMapper.mapGQLModel({ ...e.node, ruleNumber: rule.number })),
            pageInfo: instances.pageInfo,
            totalCount: instances.totalCount,
          },
        };
      }
    );
  }

  async getInstances(
    reportId: number,
    ruleNumber: number,
    includeFilteredItems: boolean,
    severities: Severity[],
    cursorInfo: CursorInfo
  ): Promise<PaginationResult<Instance>> {
    return this.query(
      GET_ISSUE_INSTANCES_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ruleNumber: ruleNumber.toString(),
        includeFilteredRules: includeFilteredItems,
        instancesKeptByFilters: includeFilteredItems ? null : true,
        filters: this.severitiesToFilter(severities),
        ...this.mapCursorInfo(cursorInfo),
      },
      (data) => {
        if (!data?.node?.rules?.edges?.[0]?.node) {
          throw new InvalidUrlError(`Invalid request: could not find Rule ${ruleNumber} in Report ${reportId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.rules.edges[0].node.instances;
        return {
          data: edges.map((e: any) => this.instanceMapper.mapGQLModel({ ...e.node, ruleNumber: data.node.rules.edges[0].node.number })),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  private async getIssueWithCenteredInstances(
    reportId: number,
    issueNumber: number,
    includeFilteredItems: boolean,
    initialInstance: InitialInstancePayload
  ): Promise<{ issue: Issue; initialInstances: PaginationResult<Instance>; instanceCounters: InstanceCounters }> {
    return this.query(
      GET_ISSUE_CENTERED_INSTANCES_QUERY,
      {
        reportId: this.gqlIdEncoder.encode(ModelType.Report, reportId),
        ruleNumber: issueNumber.toString(),
        instanceNumberLocal: initialInstance.numberLocal.toString(),
        includeFilteredRules: includeFilteredItems,
        instancesKeptByFilters: includeFilteredItems ? null : true,
      },
      (data) => {
        if (!data?.node?.rules?.edges?.[0]?.node) {
          throw new InvalidUrlError(`Invalid request: could not find Rule ${issueNumber} in Report ${reportId}`);
        }
        const { currentInstance, previousInstances, nextInstances, ...rule } = data.node.rules.edges[0].node;

        // At this point we have:
        // - the current instance
        // - at most DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1 previous instances
        // - at most DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1 next instances
        //
        // There can be more previous and next, and their amount is returned in each
        // connection's `totalCount`.
        //
        // Previous and next instances will be sorted by (`severity` DESC, `number` ASC),
        // which is equivalent to `numberLocal`.
        //
        // In total, we are dealing with at most (2 * DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1)
        // instances, which we need to reduce to DEFAULT_INSTANCES_LIST_PAGE_SIZE trimming
        // at the edges and leaving the current instance in the page.
        //
        // We also know how many instances there are in total, how many after the current,
        // and how many before the current.
        //
        // We cannot rely on the current instance's `numberLocal` because we may be filtering
        // by the instance `filterStatus`, which breaks the contiguity assumption.
        //
        // We must ensure pagination is correct: previous pages must be complete, next
        // pages can be incomplete.

        const { edges: previousEdges, totalCount: previousTotalCount } = previousInstances;
        const { edges: nextEdges, totalCount: nextTotalCount } = nextInstances;
        const { edges: currentEdges } = currentInstance;

        let instances = [];
        let hasPreviousPage = false;
        let hasNextPage = false;
        let currentPage = 0;
        if (previousTotalCount % DEFAULT_INSTANCES_LIST_PAGE_SIZE === 0) {
          // This instance is the absolute first or the first of a page
          // Return it as first, then all the next, trimmed to fit a page
          instances = [...currentEdges, ...nextEdges].slice(0, DEFAULT_INSTANCES_LIST_PAGE_SIZE);
          hasPreviousPage = previousTotalCount !== 0;
          hasNextPage = nextTotalCount > DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1;
          currentPage = Math.floor(previousTotalCount / DEFAULT_INSTANCES_LIST_PAGE_SIZE);
        } else if (previousTotalCount % DEFAULT_INSTANCES_LIST_PAGE_SIZE === DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1) {
          // This instance is the absolute last or the last of a page
          // Return all the previous, then the current as last
          instances = [...previousEdges, ...currentEdges];
          hasPreviousPage = previousTotalCount !== DEFAULT_INSTANCES_LIST_PAGE_SIZE - 1;
          hasNextPage = nextTotalCount > 0;
          currentPage = Math.floor(previousTotalCount / DEFAULT_INSTANCES_LIST_PAGE_SIZE);
        } else {
          // This instance is in the middle of a page.
          // From the previousInstances, we must take the smallest number
          // of instances that preserves complete previous pages, then insert the current
          // instance, then all the next instances and trim to fit a page size
          const selectedPreviousInstances = previousTotalCount % DEFAULT_INSTANCES_LIST_PAGE_SIZE;
          const selectedNextEdges = DEFAULT_INSTANCES_LIST_PAGE_SIZE - (selectedPreviousInstances + 1);
          instances = [...previousEdges.slice(-selectedPreviousInstances), ...currentEdges, ...nextEdges.slice(0, selectedNextEdges)];
          hasPreviousPage = selectedPreviousInstances < previousTotalCount;
          hasNextPage = selectedNextEdges < nextTotalCount;
          currentPage = Math.floor(previousTotalCount / DEFAULT_INSTANCES_LIST_PAGE_SIZE);
        }

        const first = instances[0];
        const last = instances[instances.length - 1];

        return {
          issue: this.issueMapper.mapGQLModel(rule),
          instanceCounters: this.issueMapper.mapInstanceCounters(rule),
          initialInstances: {
            data: instances.map((e: any) => this.instanceMapper.mapGQLModel({ ...e.node, ruleNumber: rule.number })),
            pageInfo: {
              hasNextPage,
              hasPreviousPage,
              startCursor: first?.cursor ?? null,
              endCursor: last?.cursor ?? null,
            },
            totalCount: previousTotalCount + nextTotalCount + currentEdges.length, // Do not sum 1, there could be no instance at all
            currentPage,
          },
        };
      }
    );
  }

  private severitiesToFilter(severities: Severity[]): FilterOperator | FilterOperation | null {
    const allSeverities = [Severity.HIGH, Severity.MED, Severity.LOW, Severity.PASSED];
    const excluded = allSeverities.filter((s) => !severities.includes(s));

    if (excluded.length === 0) {
      // All severities should be included, do not filter
      return null;
    }
    if (excluded.length === 1) {
      // Only one severity should be excluded, return a top-level filter
      return this.severityToFilter(excluded[0]);
    }
    // More than one severity should be excluded, return a composite filter
    return {
      operator: Operator.AND,
      subFilters: excluded.map((s) => this.severityToFilter(s)),
    };
  }

  private severityToFilter(severity: Severity): FilterOperation {
    return {
      field: 'severity',
      operation: Operation.NEQ,
      value: this.severityMapper.mapToGQLSeverity(severity),
    };
  }
}
